/**
 * Flag — a 3:2 SVG flag from the `country-flag-icons` package.
 *
 * Takes an ISO 3166-1 alpha-2 code (`'GB'`, `'us'`, etc.) and renders
 * the matching React component. Falls back to a neutral globe glyph
 * on miss so unexpected codes (empty strings from VPN traffic,
 * three-letter "XX" codes, etc.) don't blow up the row.
 *
 * Used on every surface that surfaces a country: the Geography
 * breakdown row, the Performance overview's Country picker, and the
 * Country ranking card on Performance.
 *
 * The library indexes flag components by uppercase ISO code in a
 * namespace export — we look them up via a typed cast rather than
 * 250 explicit imports so adding flags later is zero work.
 */
import { cn } from '@/lib/utils';
import { Globe } from '@phosphor-icons/react';
import * as Flags from 'country-flag-icons/react/3x2';
import type { ComponentType, ReactElement, SVGProps } from 'react';

// `country-flag-icons` ships one named export per country (`GB`,
// `US`, …). Cast once so the lookup site stays simple.
const FLAG_BY_CODE = Flags as unknown as Record<string, ComponentType<SVGProps<SVGSVGElement>>>;

export function Flag({
  code,
  className,
  title,
}: {
  code: string;
  className?: string;
  title?: string;
}): ReactElement {
  const upper = (code ?? '').toUpperCase();
  const FlagComponent = upper.length === 2 ? FLAG_BY_CODE[upper] : undefined;
  if (FlagComponent) {
    return (
      <FlagComponent
        className={cn('block shrink-0 rounded-[1px]', className)}
        aria-label={title ?? upper}
      />
    );
  }
  return (
    <Globe
      aria-hidden="true"
      className={cn('block shrink-0 text-[var(--color-mute)]', className)}
    />
  );
}
